import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Loader2,
  Calendar as CalendarIcon,
  Plus,
  Trash2,
  ChevronDown,
  MapPin,
  Check,
} from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/hooks/use-toast';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from 'src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  useAssignmentPartners,
  useRequirementLocations,
} from 'src/services/api/queries/opportunityQueries';
import { usePartnerList } from 'src/services/api/queries/partnerQueries';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';

const formatDateToDDMMYYYY = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseApiDate = (dateString) => {
  if (!dateString) return null;
  if (dateString.includes('/')) {
    const [dd, mm, yyyy] = dateString.split('/');
    if (dd && mm && yyyy) {
      return new Date(`${yyyy}-${mm}-${dd}`);
    }
  }
  const parsed = new Date(dateString);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getPartnerName = (partner) =>
  partner?.name ||
  partner?.organization_name ||
  partner?.org_name ||
  partner?.alias ||
  'Partner';

const getPartnerInitials = (partnerName) => {
  if (!partnerName) return 'P';
  const parts = partnerName.trim().split(' ').filter(Boolean);
  const first = parts[0]?.charAt(0) || 'P';
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
};

const SelectPartnerModal = ({
  isOpen,
  onClose,
  onSubmit,
  opportunityId,
  assignmentId,
}) => {
  const { toast } = useToast();
  const {
    data: partnerListData,
    isLoading: isPartnerLoading,
    error: partnerError,
  } = usePartnerList({
    enabled: isOpen,
    page: 1,
    pageSize: 100,
    partnerType: 1,
  });
  const {
    data: requirementLocationsResponse,
    isLoading: isRequirementLocationsLoading,
    isError: isRequirementLocationsError,
  } = useRequirementLocations(opportunityId, {
    enabled: isOpen,
    assignmentId,
  });
  const { data: assignmentPartnersResponse, error: assignmentPartnersError } =
    useAssignmentPartners(assignmentId, isOpen);

  const todayDate = new Date().toISOString().split('T')[0];
  const todayStart = new Date(todayDate);
  const requirementLocations = useMemo(
    () =>
      requirementLocationsResponse?.data?.assignment_locations ||
      requirementLocationsResponse?.data?.requirement_locations ||
      [],
    [requirementLocationsResponse]
  );

  const defaultDeadline = useMemo(() => {
    const fromAssignmentInfo =
      requirementLocationsResponse?.data?.assignment_info?.deadline;
    return parseApiDate(fromAssignmentInfo);
  }, [requirementLocationsResponse]);

  const locationMeta = useMemo(
    () =>
      requirementLocations
        .map((loc) => {
          const locId =
            loc?.requirement_location_id || loc?.location_id || loc?.id;
          if (!locId) return null;
          const locationName = loc?.location_name || 'Unknown Location';
          const employmentType = loc?.employment_type;
          const requestedRaw = Number(loc?.candidates_requested);
          const submittedRaw = Number(loc?.candidates_submitted);
          const requestedCount = Number.isFinite(requestedRaw)
            ? requestedRaw
            : null;
          const submittedCount = Number.isFinite(submittedRaw)
            ? submittedRaw
            : 0;
          const isFull =
            requestedCount !== null ? submittedCount >= requestedCount : false;
          return {
            id: locId,
            locationName,
            employmentType,
            label: employmentType
              ? `${locationName} • ${employmentType}`
              : locationName,
            isFull,
          };
        })
        .filter(Boolean),
    [requirementLocations]
  );

  const getDefaultSelectedLocationIds = () =>
    locationMeta.filter((loc) => !loc.isFull).map((loc) => loc.id);

  const createAssignment = (partner) => ({
    id: Date.now() + Math.random(),
    partnerId: partner?.id ? partner.id.toString() : '',
    partnerOrgId: partner?.org_id || '',
    partnerName: partner ? getPartnerName(partner) : '',
    dueDate: defaultDeadline || null,
    dueDateLimit: defaultDeadline || null,
    selectedLocationIds: getDefaultSelectedLocationIds(),
    lockedLocationIds: [],
    isExpanded: true,
  });

  const [assignments, setAssignments] = useState([]);
  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [isAddPartnerOpen, setIsAddPartnerOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);

  const usedPartnerIds = useMemo(
    () => assignments.map((assignment) => assignment.partnerId).filter(Boolean),
    [assignments]
  );

  const partners =
    partnerListData?.partners ||
    partnerListData?.data?.partners ||
    partnerListData?.items ||
    [];

  useEffect(() => {
    if (partnerError) {
      toast({
        message: partnerError.message || 'Failed to fetch partner list',
        variant: 'destructive',
      });
    }
  }, [partnerError, toast]);

  useEffect(() => {
    if (assignmentPartnersError) {
      toast({
        message:
          assignmentPartnersError.message ||
          'Failed to fetch assigned partners',
        variant: 'destructive',
      });
    }
  }, [assignmentPartnersError, toast]);

  useEffect(() => {
    if (!isOpen || hasPrefilled) return;
    const assignmentPartnersRaw =
      assignmentPartnersResponse?.data?.data ||
      assignmentPartnersResponse?.data ||
      [];
    if (
      !Array.isArray(assignmentPartnersRaw) ||
      !assignmentPartnersRaw.length
    ) {
      return;
    }

    const prefilled = assignmentPartnersRaw.map((assignment, index) => {
      const partnerOrgId = assignment?.partner_org_id || '';
      const partner = partners.find((item) => item.org_id === partnerOrgId);
      const partnerName = partner ? getPartnerName(partner) : 'Partner';
      const partnerId = partner?.id ? partner.id.toString() : '';
      const deadlineDate = parseApiDate(assignment?.deadline);
      const selectedLocationIds = (assignment?.mapped_locations || [])
        .map((loc) => loc?.requirement_location_id)
        .filter(Boolean);

      return {
        id:
          assignment?.partner_assignment_id ||
          `existing-${partnerOrgId}-${index}`,
        partnerId,
        partnerOrgId,
        partnerName,
        dueDate: deadlineDate || null,
        dueDateLimit: deadlineDate || defaultDeadline || null,
        selectedLocationIds:
          selectedLocationIds.length > 0
            ? selectedLocationIds
            : getDefaultSelectedLocationIds(),
        lockedLocationIds: [],
        isExisting: true,
        isExpanded: index === 0,
      };
    });

    setAssignments(prefilled);
    setHasPrefilled(true);
  }, [
    assignmentPartnersResponse,
    defaultDeadline,
    getDefaultSelectedLocationIds,
    hasPrefilled,
    isOpen,
    partners,
  ]);

  const addAssignment = (partner) => {
    setAssignments((prev) => [
      ...prev.map((assignment) => ({ ...assignment, isExpanded: false })),
      createAssignment(partner),
    ]);
  };

  const handleAddPartner = (partnerId) => {
    if (!partnerId || usedPartnerIds.includes(partnerId)) return;
    const partner = partners.find((item) => item.id.toString() === partnerId);
    if (!partner) return;
    addAssignment(partner);
    setIsAddPartnerOpen(false);
  };

  const removeAssignment = (id) => {
    setAssignments((prev) =>
      prev.length === 1 ? prev : prev.filter((a) => a.id !== id)
    );
  };

  const resetForm = () => {
    setAssignments([]);
    setIsAddPartnerOpen(false);
    setHasPrefilled(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (!assignments.length) {
      toast({
        message: 'Please add at least one partner.',
        variant: 'warning',
      });
      return;
    }
    const duplicateCheck = new Set();
    for (let i = 0; i < assignments.length; i += 1) {
      const assignment = assignments[i];
      if (!assignment.partnerId) {
        toast({
          message: `Please select a partner for row ${i + 1}`,
          variant: 'warning',
        });
        return;
      }
      if (!assignment.partnerOrgId) {
        toast({
          message: `Missing partner org id for row ${i + 1}`,
          variant: 'warning',
        });
        return;
      }
      if (duplicateCheck.has(assignment.partnerId)) {
        toast({
          message: 'Each partner can only be assigned once',
          variant: 'warning',
        });
        return;
      }
      duplicateCheck.add(assignment.partnerId);

      if (!assignment.dueDate) {
        toast({
          message: `Please select a due date for row ${i + 1}`,
          variant: 'warning',
        });
        return;
      }

      if (assignment.dueDate < todayStart) {
        toast({
          message: `Due date for row ${i + 1} cannot be in the past`,
          variant: 'warning',
        });
        return;
      }

      if (!requirementLocations.length) {
        toast({
          message: 'No location data available to assign.',
          variant: 'warning',
        });
        return;
      }

      if (!assignment.selectedLocationIds?.length) {
        toast({
          message: `Please select at least one location for row ${i + 1}`,
          variant: 'warning',
        });
        return;
      }
    }

    const payload = assignments.map((assignment) => {
      const locationsPayload = (assignment.selectedLocationIds || [])
        .filter(Boolean)
        .map((id) => ({ requirement_location_id: id }));
      return {
        partner_org_id: assignment.partnerOrgId,
        requirement_locations: locationsPayload,
        deadline: formatDateToDDMMYYYY(assignment.dueDate),
      };
    });

    const selectedLocationIds = new Set(
      payload.flatMap((item) =>
        (item.requirement_locations || []).map(
          (loc) => loc.requirement_location_id
        )
      )
    );
    const totalRequired = requirementLocations.reduce((sum, loc) => {
      const locId = loc?.requirement_location_id || loc?.location_id || loc?.id;
      if (!locId || !selectedLocationIds.has(locId)) return sum;
      return sum + (Number(loc?.candidates_requested) || 0);
    }, 0);

    onSubmit({
      assignments: payload,
      totalRequired,
      assignmentId,
    });
    handleClose();
  };

  const renderRequirementLocationsSummary = () => {
    if (isRequirementLocationsLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading location headcount...
        </div>
      );
    }

    if (isRequirementLocationsError) {
      return (
        <p className="text-sm text-destructive">
          Could not load location-wise headcount. You can still enter total
          headcount below.
        </p>
      );
    }

    if (!requirementLocations.length) {
      return (
        <p className="text-sm text-muted-foreground">
          No location data available.
        </p>
      );
    }

    return (
      <div className="bg-muted/50 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.9fr] bg-muted/40 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          <span className="px-4 py-2">Location / Job Type</span>
          <span className="md:text-center px-4 py-2">Required</span>
          <span className="md:text-center px-4 py-2">Submitted</span>
        </div>
        <div className="divide-y divide-border">
          {requirementLocations.map((loc, index) => {
            const locId =
              loc?.requirement_location_id || loc?.location_id || loc?.id;
            const locationName = loc?.location_name;
            const employmentType = loc?.employment_type;
            const requiredHeadcount = loc?.candidates_requested ?? '0';
            const submittedCount =
              loc?.candidates_submitted ?? loc?.submitted_count ?? 0;

            return (
              <div
                key={locId || `${locationName}-${index}`}
                className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.9fr] items-stretch gap-3 md:gap-2"
              >
                <div className="flex gap-1 items-center px-4 py-2">
                  <span className="text-sm font-medium text-foreground">
                    {locationName}
                  </span>
                  {employmentType && (
                    <span className="text-[11px] text-muted-foreground">
                      - {employmentType}
                    </span>
                  )}
                </div>
                <div className="flex items-center md:justify-center px-4 py-2">
                  <span className="text-base font-semibold text-foreground">
                    {requiredHeadcount}
                  </span>
                </div>
                <div className="flex items-center md:justify-center px-4 py-2">
                  <span className="text-base font-semibold text-ds-success">
                    {submittedCount}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAssignmentForm = (assignment, index) => {
    const lockedLocationIds = new Set(assignment.lockedLocationIds || []);
    const selectedLocationIds = new Set(assignment.selectedLocationIds || []);
    const isExpanded = assignment.isExpanded ?? index === 0;

    return (
      <div
        key={assignment.id}
        className={`group border rounded-lg p-4 space-y-4 ${isExpanded ? 'border-primary' : 'border-border'}`}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium text-sm shadow-sm">
            {getPartnerInitials(assignment.partnerName)}
          </div>
          <div className="flex flex-1 items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground-strong">
                {assignment.partnerName || 'Partner'}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedLocationIds.size} locations</span>
                </span>
                {assignment.dueDate && (
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span>{format(assignment.dueDate, 'MMM d, yyyy')}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-0.5">
              {assignments.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAssignment(assignment.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setAssignments((prev) =>
                    prev.map((a) =>
                      a.id === assignment.id
                        ? { ...a, isExpanded: !isExpanded }
                        : { ...a, isExpanded: false }
                    )
                  )
                }
                className="text-muted-foreground hover:text-foreground"
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </Button>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-sm text-muted-foreground">
                Due Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {assignment.dueDate ? (
                      format(assignment.dueDate, 'PPP')
                    ) : (
                      <span>Select date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={assignment.dueDate || undefined}
                    onSelect={(date) =>
                      setAssignments((prev) =>
                        prev.map((a) =>
                          a.id === assignment.id
                            ? { ...a, dueDate: date || null }
                            : a
                        )
                      )
                    }
                    disabled={(date) =>
                      date < todayStart ||
                      (assignment.dueDateLimit &&
                        date > assignment.dueDateLimit)
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-sm text-muted-foreground">
                Assigned Locations
              </Label>
              <div className="flex flex-wrap gap-2">
                {locationMeta.map((loc) => {
                  const isSelected = selectedLocationIds.has(loc.id);
                  const isLocked = lockedLocationIds.has(loc.id);
                  const isDisabled = loc.isFull;
                  const canToggle = !isLocked && !isDisabled;
                  return (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => {
                        if (!canToggle) return;
                        setAssignments((prev) =>
                          prev.map((a) => {
                            if (a.id !== assignment.id) return a;
                            const nextIds = new Set(
                              a.selectedLocationIds || []
                            );
                            if (nextIds.has(loc.id)) {
                              nextIds.delete(loc.id);
                            } else {
                              nextIds.add(loc.id);
                            }
                            return {
                              ...a,
                              selectedLocationIds: Array.from(nextIds),
                            };
                          })
                        );
                      }}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        isSelected
                          ? 'bg-ds-success-subtle text-ds-success border-ds-success-tint'
                          : 'bg-muted text-muted-foreground border-border'
                      } ${canToggle ? 'hover:shadow-sm' : 'opacity-70'}`}
                    >
                      {isSelected && <Check className="h-3.5 w-3.5" />}
                      {loc.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[42rem]">
        <DialogHeader>
          <DialogTitle>Assign Partner</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {isPartnerLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="sticky top-0 z-10 bg-card shadow-md ring-1 ring-border rounded-lg">
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-sm font-semibold text-foreground">
                    Location Summary
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSummaryOpen((prev) => !prev)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        isSummaryOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </Button>
                </div>
                {isSummaryOpen && renderRequirementLocationsSummary()}
              </div>
              <div className="space-y-4">
                {assignments.map((assignment, idx) =>
                  renderAssignmentForm(assignment, idx)
                )}
              </div>

              <div>
                <Select
                  open={isAddPartnerOpen}
                  onOpenChange={setIsAddPartnerOpen}
                  onValueChange={handleAddPartner}
                >
                  <SelectTrigger className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed rounded-xl font-medium transition-all border-input text-primary hover:border-primary hover:bg-brand-bg-subtle focus:outline-none focus:ring-0">
                    <Plus className="h-4 w-4" />
                    Add Partner
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((partner) => {
                      const partnerIdStr = partner.id.toString();
                      const isUsedElsewhere =
                        usedPartnerIds.includes(partnerIdStr);
                      const partnerName = getPartnerName(partner);
                      return (
                        <SelectItem
                          key={partner.id}
                          value={partnerIdStr}
                          disabled={isUsedElsewhere}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-medium text-xs gap-2">
                              {getPartnerInitials(partnerName)}
                            </div>
                            <span className="text-sm font-medium text-foreground-strong">
                              {partnerName}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPartnerLoading}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectPartnerModal;
